import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { AdminLayout } from "@/pages/AdminDashboard";
import api from "@/services/api";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import {
  Search,
  Filter,
  Download,
  Eye,
  FileText,
  ChevronDown,
  X,
  FileSpreadsheet,
  Trash2,
  Pencil
} from "lucide-react";

const STATUSES = ["new", "contacted", "qualified", "closed"];
const BUDGETS = [
  "Below ₹50 Lakhs",
  "₹50 Lakhs – ₹75 Lakhs",
  "₹75 Lakhs – ₹1.5 Crores",
  "₹1.5 CR – ₹3 CR",
  "₹3 CR – ₹5 CR",
  "₹5 Crores+"
];

export default function AdminSubmissions() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    status: "",
    budget_range: "",
    date_from: "",
    date_to: ""
  });
  const [showFilters, setShowFilters] = useState(false);

  const fetchSubmissions = useCallback(async () => {
    if (!token) return;
    try {
      const params = { ...filters };
      if (search) params.search = search;

      const response = await api.getSubmissions(params, token);
      if (!response.error) {
        setSubmissions(response.submissions || []);
      } else {
        toast.error("Failed to fetch submissions");
      }
    } catch (error) {
      toast.error("Failed to fetch submissions");
    } finally {
      setLoading(false);
    }
  }, [token, filters, search]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchSubmissions();
  };

  const exportCSV = async () => {
    try {
      const response = await api.exportData(token);
      if (response.submissions) {
        // Convert to CSV
        const headers = ["Reference ID", "Couple Names", "Phone", "Wedding Date", "Guest Count", "Destination", "Events", "Themes", "Services", "Budget Range", "Has Rituals", "Status", "Created At"];
        const rows = response.submissions.map(s => [
          s.reference_id,
          s.couple_names,
          s.phone,
          s.wedding_date,
          s.guest_count,
          s.wedding_destination || '',
          Array.isArray(s.events) ? s.events.join("; ") : s.events,
          Array.isArray(s.styling_themes) ? s.styling_themes.join("; ") : (s.styling_themes || ''),
          Array.isArray(s.services_required) ? s.services_required.join("; ") : s.services_required,
          s.budget_range,
          s.has_rituals ? 'Yes' : 'No',
          s.status,
          s.created_at
        ]);
        
        const csv = [headers, ...rows].map(row => row.map(cell => `"${cell || ''}"`).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `submissions-export-${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        toast.success("CSV exported successfully");
      }
    } catch (error) {
      toast.error("Export failed");
    }
  };

  const exportExcel = async () => {
    try {
      const response = await api.exportData(token);
      if (response.submissions) {
        const data = response.submissions.map(s => ({
          "Reference ID": s.reference_id,
          "Couple Names": s.couple_names,
          "Phone": s.phone,
          "Wedding Date": s.wedding_date,
          "Guest Count": s.guest_count,
          "Destination": s.wedding_destination || '',
          "Events": Array.isArray(s.events) ? s.events.join("; ") : s.events,
          "Themes": Array.isArray(s.styling_themes) ? s.styling_themes.join("; ") : (s.styling_themes || ''),
          "Vision Details": s.styling_preferences || '',
          "Services": Array.isArray(s.services_required) ? s.services_required.join("; ") : s.services_required,
          "Budget Range": s.budget_range,
          "Has Rituals": s.has_rituals ? 'Yes' : 'No',
          "Ritual Details": s.ritual_details || '',
          "Status": s.status,
          "Created At": s.created_at
        }));
        
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Submissions");
        
        // Auto-size columns
        const colWidths = Object.keys(data[0] || {}).map(key => ({ wch: Math.max(key.length, 15) }));
        ws['!cols'] = colWidths;
        
        XLSX.writeFile(wb, `submissions-export-${new Date().toISOString().split('T')[0]}.xlsx`);
        toast.success("Excel exported successfully");
      }
    } catch (error) {
      toast.error("Export failed");
    }
  };

  const clearFilters = () => {
    setFilters({ status: "", budget_range: "", date_from: "", date_to: "" });
    setSearch("");
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === submissions.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(submissions.map((s) => s.id));
    }
  };

  const handleDeleteOne = async (submission) => {
    if (!window.confirm(`Delete submission ${submission.reference_id} (${submission.couple_names})? This cannot be undone.`)) {
      return;
    }
    setDeleting(true);
    try {
      const response = await api.deleteSubmission(submission.id, token);
      if (response.error) {
        toast.error(response.error);
      } else {
        setSubmissions((prev) => prev.filter((s) => s.id !== submission.id));
        setSelectedIds((prev) => prev.filter((id) => id !== submission.id));
        toast.success("Submission deleted");
      }
    } catch {
      toast.error("Failed to delete submission");
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} selected submission(s)? This cannot be undone.`)) {
      return;
    }
    setDeleting(true);
    try {
      const response = await api.deleteSubmissions(selectedIds, token);
      if (response.error) {
        toast.error(response.error);
      } else {
        setSubmissions((prev) => prev.filter((s) => !selectedIds.includes(s.id)));
        setSelectedIds([]);
        toast.success(`${response.deleted || selectedIds.length} submission(s) deleted`);
      }
    } catch {
      toast.error("Failed to delete submissions");
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  };

  const renderActions = (submission, mobile = false) => (
    <div className={`flex items-center gap-2 ${mobile ? "w-full" : ""}`}>
      <Link
        to={`/admin/submissions/${submission.id}`}
        className={`text-[#c4646a] hover:text-[#d4848a] flex items-center justify-center gap-1.5 rounded-lg border border-[#c4646a]/30 bg-[#c4646a]/10 hover:bg-[#c4646a]/20 transition-colors ${
          mobile ? "flex-1 py-2.5 text-sm" : "p-2"
        }`}
        title="View"
        data-testid={`view-${submission.id}`}
      >
        <Eye className="w-4 h-4" />
        {mobile && <span>View</span>}
      </Link>
      <button
        type="button"
        onClick={() => navigate(`/admin/submissions/${submission.id}?edit=1`)}
        className={`text-blue-400 hover:text-blue-300 flex items-center justify-center gap-1.5 rounded-lg border border-blue-400/30 bg-blue-400/10 hover:bg-blue-400/20 transition-colors ${
          mobile ? "flex-1 py-2.5 text-sm" : "p-2"
        }`}
        title="Edit"
        data-testid={`edit-${submission.id}`}
      >
        <Pencil className="w-4 h-4" />
        {mobile && <span>Edit</span>}
      </button>
      <button
        type="button"
        onClick={() => handleDeleteOne(submission)}
        disabled={deleting}
        className={`text-red-400 hover:text-red-300 flex items-center justify-center gap-1.5 rounded-lg border border-red-400/30 bg-red-400/10 hover:bg-red-400/20 transition-colors disabled:opacity-50 ${
          mobile ? "flex-1 py-2.5 text-sm" : "p-2"
        }`}
        title="Delete"
        data-testid={`delete-${submission.id}`}
      >
        <Trash2 className="w-4 h-4" />
        {mobile && <span>Delete</span>}
      </button>
    </div>
  );

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 md:p-8 w-full min-w-0 max-w-full" data-testid="admin-submissions">
        {/* Header */}
        <div className="flex flex-col gap-4 mb-6 md:mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-light text-white">Submissions</h1>
            <p className="text-gray-400 mt-1 text-sm sm:text-base">{submissions.length} total submissions</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:flex-wrap">
            {selectedIds.length > 0 && (
              <button
                onClick={handleBulkDelete}
                disabled={deleting}
                className="flex items-center justify-center gap-2 px-4 py-3 sm:py-2 bg-red-600/90 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 w-full sm:w-auto"
                data-testid="btn-bulk-delete"
              >
                <Trash2 className="w-4 h-4" />
                Delete Selected ({selectedIds.length})
              </button>
            )}
            <button
              onClick={exportCSV}
              className="flex items-center justify-center gap-2 px-4 py-3 sm:py-2 bg-white/5 border border-white/10 rounded-lg text-white hover:bg-white/10 transition-colors w-full sm:w-auto"
              data-testid="btn-export-csv"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
            <button
              onClick={exportExcel}
              className="flex items-center justify-center gap-2 px-4 py-3 sm:py-2 bg-[#c4646a] text-white rounded-lg hover:bg-[#a85459] transition-colors w-full sm:w-auto"
              data-testid="btn-export-excel"
            >
              <FileSpreadsheet className="w-4 h-4" />
              Export Excel
            </button>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <form onSubmit={handleSearch} className="flex-1 min-w-0">
              <div className="relative">
                <Search className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name, reference, phone..."
                  className="luxury-input pl-10 sm:pl-12 py-3 w-full"
                  data-testid="input-search"
                />
              </div>
            </form>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border transition-colors w-full sm:w-auto shrink-0 ${
                showFilters
                  ? "bg-[#c4646a]/20 border-[#c4646a] text-[#c4646a]"
                  : "bg-white/5 border-white/10 text-white hover:bg-white/10"
              }`}
            >
              <Filter className="w-5 h-5" />
              Filters
              <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? "rotate-180" : ""}`} />
            </button>
          </div>

          {/* Filter Options */}
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-white/10">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  className="luxury-input py-3"
                  data-testid="filter-status"
                >
                  <option value="">All Statuses</option>
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Budget</label>
                <select
                  value={filters.budget_range}
                  onChange={(e) => setFilters({ ...filters, budget_range: e.target.value })}
                  className="luxury-input py-3"
                  data-testid="filter-budget"
                >
                  <option value="">All Budgets</option>
                  {BUDGETS.map((b) => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <button
                  onClick={clearFilters}
                  className="text-[#c4646a] hover:text-[#d4848a] text-sm flex items-center gap-1"
                >
                  <X className="w-4 h-4" />
                  Clear all filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* List */}
        <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden w-full min-w-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#c4646a]"></div>
            </div>
          ) : submissions.length === 0 ? (
            <div className="text-center py-16 px-4">
              <FileText className="w-16 h-16 mx-auto text-gray-600 mb-4" />
              <p className="text-gray-400">No submissions found</p>
            </div>
          ) : (
            <>
              {/* Mobile cards */}
              <div className="md:hidden divide-y divide-white/10">
                {submissions.length > 0 && (
                  <div className="flex items-center justify-between px-4 py-3 bg-white/[0.02]">
                    <label className="flex items-center gap-2 text-sm text-gray-400">
                      <input
                        type="checkbox"
                        checked={selectedIds.length === submissions.length}
                        onChange={toggleSelectAll}
                        className="rounded border-white/20 bg-white/5 w-4 h-4"
                        aria-label="Select all submissions"
                      />
                      Select all
                    </label>
                    <span className="text-xs text-gray-500">{selectedIds.length} selected</span>
                  </div>
                )}
                {submissions.map((submission) => (
                  <div key={submission.id} className="p-4">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(submission.id)}
                        onChange={() => toggleSelect(submission.id)}
                        className="rounded border-white/20 bg-white/5 w-4 h-4 mt-1 shrink-0"
                        aria-label={`Select ${submission.reference_id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="min-w-0">
                            <p className="font-mono text-xs text-gray-500 truncate">{submission.reference_id}</p>
                            <p className="text-white font-medium leading-snug break-words">{submission.couple_names}</p>
                          </div>
                          <span className={`status-badge status-${submission.status} shrink-0 text-xs`}>
                            {submission.status}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm mb-4">
                          <div>
                            <p className="text-gray-500 text-xs">Wedding</p>
                            <p className="text-gray-200">{submission.wedding_date || "-"}</p>
                          </div>
                          <div>
                            <p className="text-gray-500 text-xs">Guests</p>
                            <p className="text-gray-200">{submission.guest_count || "-"}</p>
                          </div>
                          <div className="col-span-2">
                            <p className="text-gray-500 text-xs">Budget</p>
                            <p className="text-gray-200 break-words">{submission.budget_range || "-"}</p>
                          </div>
                          <div className="col-span-2">
                            <p className="text-gray-500 text-xs">Created</p>
                            <p className="text-gray-200">{formatDate(submission.created_at)}</p>
                          </div>
                        </div>
                        {renderActions(submission, true)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th className="w-10">
                        <input
                          type="checkbox"
                          checked={submissions.length > 0 && selectedIds.length === submissions.length}
                          onChange={toggleSelectAll}
                          className="rounded border-white/20 bg-white/5"
                          aria-label="Select all submissions"
                        />
                      </th>
                      <th>Reference</th>
                      <th>Couple Names</th>
                      <th>Wedding Date</th>
                      <th>Guests</th>
                      <th>Budget</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {submissions.map((submission) => (
                      <tr key={submission.id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(submission.id)}
                            onChange={() => toggleSelect(submission.id)}
                            className="rounded border-white/20 bg-white/5"
                            aria-label={`Select ${submission.reference_id}`}
                          />
                        </td>
                        <td className="font-mono text-sm">{submission.reference_id}</td>
                        <td>{submission.couple_names}</td>
                        <td>{submission.wedding_date}</td>
                        <td>{submission.guest_count}</td>
                        <td className="text-sm">{submission.budget_range}</td>
                        <td>
                          <span className={`status-badge status-${submission.status}`}>
                            {submission.status}
                          </span>
                        </td>
                        <td className="text-sm text-gray-400">
                          {formatDate(submission.created_at)}
                        </td>
                        <td>{renderActions(submission)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
